import React, { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";
import { useCoA } from "../../context/CoAContext";
import { v4 as uuidv4 } from "uuid";
import "./JournalForm.css";
import {
  PlusSquare,
  Save,
  CalendarDays,
  Scale,
  Info,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";

const createEmptyRow = () => ({
  account_id: "",
  description: "",
  money_in: 0,
  money_out: 0,

  // tax fields
  tax_type: "",
  tax_base_amount: 0,
  tax_rate: 0,
  tax_amount: 0,
  taxable_flag: false,
  recoverable_flag: false,
  exempt_flag: false,
  zero_rated_flag: false,
  withholding_flag: false,
  deductible_flag: true,
  showTaxAdvanced: false,
});

const round2 = (n) => Number(Number(n || 0).toFixed(2));

const JournalForm = () => {
  const { companyId } = useCompany();
  const { accounts } = useCoA();

  const [date, setDate] = useState("");
  const [journalRows, setJournalRows] = useState([
    createEmptyRow(),
    createEmptyRow(),
  ]);
  const [journals, setJournals] = useState([]);
  const [loading, setLoading] = useState(false);

  // ===== Fetch existing journals (kept feature, safer grouping) =====
  const fetchJournals = useCallback(async () => {
    if (!companyId) return;

    const { data, error } = await supabase
      .from("journal_entries")
      .select("id, journal_id, date, created_at")
      .eq("company_id", companyId)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching journals:", error);
      return;
    }

    const seen = new Set();
    const grouped = [];

    for (const row of data || []) {
      if (!row.journal_id || seen.has(row.journal_id)) continue;
      seen.add(row.journal_id);
      grouped.push(row);
    }

    setJournals(grouped);
  }, [companyId]);

  useEffect(() => {
    fetchJournals();
  }, [fetchJournals]);

  // ===== Derived UI-only totals (existing feature kept) =====
  const totalDebit = useMemo(
    () => journalRows.reduce((s, r) => s + Number(r.money_in || 0), 0),
    [journalRows]
  );

  const totalCredit = useMemo(
    () => journalRows.reduce((s, r) => s + Number(r.money_out || 0), 0),
    [journalRows]
  );

  const difference = useMemo(
    () => totalDebit - totalCredit,
    [totalDebit, totalCredit]
  );

  const isBalanced =
    difference === 0 && (totalDebit !== 0 || totalCredit !== 0);

  const recalculateTax = (row) => {
    const amountBase =
      Number(row.money_in || 0) > 0
        ? Number(row.money_in || 0)
        : Number(row.money_out || 0);

    const taxBase =
      Number(row.tax_base_amount || 0) > 0
        ? Number(row.tax_base_amount || 0)
        : amountBase;

    const rate = Number(row.tax_rate || 0);
    const taxAmount = row.tax_type ? (taxBase * rate) / 100 : 0;

    return {
      ...row,
      tax_base_amount: row.tax_type ? taxBase : 0,
      tax_amount: row.tax_type ? taxAmount : 0,
    };
  };

  // ===== Handlers (existing behavior preserved, tax added lightly) =====
  const handleChange = (index, field, value) => {
    setJournalRows((prev) => {
      const updated = [...prev];
      let row = { ...updated[index] };

      const numericFields = [
        "money_in",
        "money_out",
        "tax_base_amount",
        "tax_rate",
        "tax_amount",
      ];

      const booleanFields = [
        "taxable_flag",
        "recoverable_flag",
        "exempt_flag",
        "zero_rated_flag",
        "withholding_flag",
        "deductible_flag",
        "showTaxAdvanced",
      ];

      if (numericFields.includes(field)) {
        row[field] = parseFloat(value) || 0;
      } else if (booleanFields.includes(field)) {
        row[field] = Boolean(value);
      } else {
        row[field] = value;
      }

      // smart defaults when tax type selected
      if (field === "tax_type") {
        if (!value) {
          row.tax_type = "";
          row.tax_base_amount = 0;
          row.tax_rate = 0;
          row.tax_amount = 0;
          row.taxable_flag = false;
          row.recoverable_flag = false;
          row.exempt_flag = false;
          row.zero_rated_flag = false;
          row.withholding_flag = false;
          row.deductible_flag = true;
        } else if (value === "VAT") {
          row.tax_rate = row.tax_rate || 7.5;
          row.taxable_flag = true;
          row.withholding_flag = false;
        } else if (value === "WHT") {
          row.tax_rate = row.tax_rate || 5;
          row.taxable_flag = true;
          row.withholding_flag = true;
        }
      }

      // keep debit/credit behavior same, just derive tax base automatically
      if (
        field === "money_in" ||
        field === "money_out" ||
        field === "tax_type" ||
        field === "tax_rate" ||
        field === "tax_base_amount"
      ) {
        row = recalculateTax(row);
      }

      updated[index] = row;
      return updated;
    });
  };

  const addRows = () => {
    setJournalRows((prev) => [...prev, createEmptyRow(), createEmptyRow()]);
  };

  // ===== NEW: Load mapped tax accounts =====
  const loadTaxAccountMappings = async () => {
    if (!companyId) throw new Error("Company context not loaded.");

    const { data, error } = await supabase
      .from("tax_accounts")
      .select("tax_type_code, tax_account_role, account_id, is_active")
      .eq("company_id", companyId)
      .eq("is_active", true);

    if (error) {
      throw new Error(error.message || "Failed to load tax account mappings");
    }

    const rows = data || [];

    return {
      outputVat:
        rows.find(
          (r) =>
            r.tax_type_code === "VAT" &&
            ["output_vat", "vat_output_liability"].includes(r.tax_account_role)
        )?.account_id || null,

      inputVat:
        rows.find(
          (r) =>
            r.tax_type_code === "VAT" &&
            ["input_vat", "vat_input_recoverable"].includes(r.tax_account_role)
        )?.account_id || null,

      whtPayable:
        rows.find(
          (r) =>
            r.tax_type_code === "WHT" &&
            ["wht_payable"].includes(r.tax_account_role)
        )?.account_id || null,

      whtReceivable:
        rows.find(
          (r) =>
            r.tax_type_code === "WHT" &&
            ["wht_receivable"].includes(r.tax_account_role)
        )?.account_id || null,
    };
  };

  // ===== NEW: Build final entries including tax journal lines =====
  const buildEntriesWithTax = (baseRows, journalId, taxMap) => {
    const entries = baseRows.map((row) => ({
      journal_id: journalId,
      date,
      company_id: companyId,
      account_id: row.account_id,
      description: row.description,
      money_in: Number(row.money_in || 0),
      money_out: Number(row.money_out || 0),

      posting_status: "posted",
      tax_type: row.tax_type || null,
      tax_base_amount: Number(row.tax_base_amount || 0),
      tax_rate: Number(row.tax_rate || 0),
      tax_amount: Number(row.tax_amount || 0),
      taxable_flag: !!row.taxable_flag,
      recoverable_flag: !!row.recoverable_flag,
      exempt_flag: !!row.exempt_flag,
      zero_rated_flag: !!row.zero_rated_flag,
      withholding_flag: !!row.withholding_flag,
      deductible_flag: !!row.deductible_flag,
    }));

    const firstDebitIndex = entries.findIndex(
      (r) => Number(r.money_in || 0) > 0
    );
    const firstCreditIndex = entries.findIndex(
      (r) => Number(r.money_out || 0) > 0
    );

    for (let i = 0; i < baseRows.length; i += 1) {
      const row = baseRows[i];
      const taxAmount = Number(row.tax_amount || 0);

      if (!row.tax_type || taxAmount <= 0) continue;

      // ===== VAT logic =====
      // If recoverable (e.g. purchase/input VAT):
      // - Dr VAT Recoverable
      // - increase first credit line by tax amount
      // Assumes user-entered row amount is tax-exclusive base.
      if (row.tax_type === "VAT" && !!row.recoverable_flag) {
        if (!taxMap.inputVat) {
          throw new Error(
            "VAT Recoverable account mapping is missing. Please configure tax accounts first."
          );
        }

        if (firstCreditIndex === -1) {
          throw new Error(
            "Could not find a credit line to balance recoverable VAT posting."
          );
        }

        entries.push({
          journal_id: journalId,
          date,
          company_id: companyId,
          account_id: taxMap.inputVat,
          description: `${row.description} (VAT Recoverable)`,
          money_in: taxAmount,
          money_out: 0,
          posting_status: "posted",
          tax_type: "VAT",
          tax_base_amount: Number(row.tax_base_amount || 0),
          tax_rate: Number(row.tax_rate || 0),
          tax_amount: taxAmount,
          taxable_flag: true,
          recoverable_flag: true,
          exempt_flag: !!row.exempt_flag,
          zero_rated_flag: !!row.zero_rated_flag,
          withholding_flag: false,
          deductible_flag: true,
        });

        entries[firstCreditIndex].money_out = round2(
          Number(entries[firstCreditIndex].money_out || 0) + taxAmount
        );
        continue;
      }

      // If payable/output VAT:
      // - Cr VAT Payables
      // - increase first debit line by tax amount
      if (row.tax_type === "VAT" && !row.recoverable_flag) {
        if (!taxMap.outputVat) {
          throw new Error(
            "VAT Payables account mapping is missing. Please configure tax accounts first."
          );
        }

        if (firstDebitIndex === -1) {
          throw new Error(
            "Could not find a debit line to balance VAT payable posting."
          );
        }

        entries.push({
          journal_id: journalId,
          date,
          company_id: companyId,
          account_id: taxMap.outputVat,
          description: `${row.description} (VAT Payable)`,
          money_in: 0,
          money_out: taxAmount,
          posting_status: "posted",
          tax_type: "VAT",
          tax_base_amount: Number(row.tax_base_amount || 0),
          tax_rate: Number(row.tax_rate || 0),
          tax_amount: taxAmount,
          taxable_flag: true,
          recoverable_flag: false,
          exempt_flag: !!row.exempt_flag,
          zero_rated_flag: !!row.zero_rated_flag,
          withholding_flag: false,
          deductible_flag: true,
        });

        entries[firstDebitIndex].money_in = round2(
          Number(entries[firstDebitIndex].money_in || 0) + taxAmount
        );
        continue;
      }

      // ===== WHT logic =====
      // For WHT receivable:
      // - split debit/gross row into net + WHT receivable
      if (row.tax_type === "WHT" && !!row.recoverable_flag) {
        if (!taxMap.whtReceivable) {
          throw new Error(
            "WHT Receivables account mapping is missing. Please configure tax accounts first."
          );
        }

        if (Number(entries[i].money_in || 0) <= taxAmount) {
          throw new Error(
            `WHT receivable on row ${
              i + 1
            } is invalid. Debit amount must be greater than WHT amount.`
          );
        }

        entries[i].money_in = round2(
          Number(entries[i].money_in || 0) - taxAmount
        );

        entries.push({
          journal_id: journalId,
          date,
          company_id: companyId,
          account_id: taxMap.whtReceivable,
          description: `${row.description} (WHT Receivable)`,
          money_in: taxAmount,
          money_out: 0,
          posting_status: "posted",
          tax_type: "WHT",
          tax_base_amount: Number(row.tax_base_amount || 0),
          tax_rate: Number(row.tax_rate || 0),
          tax_amount: taxAmount,
          taxable_flag: true,
          recoverable_flag: true,
          exempt_flag: false,
          zero_rated_flag: false,
          withholding_flag: true,
          deductible_flag: true,
        });

        continue;
      }

      // For WHT payable:
      // - split credit/gross row into net + WHT payable
      if (row.tax_type === "WHT" && !row.recoverable_flag) {
        if (!taxMap.whtPayable) {
          throw new Error(
            "WHT Payables account mapping is missing. Please configure tax accounts first."
          );
        }

        if (Number(entries[i].money_out || 0) <= taxAmount) {
          throw new Error(
            `WHT payable on row ${
              i + 1
            } is invalid. Credit amount must be greater than WHT amount.`
          );
        }

        entries[i].money_out = round2(
          Number(entries[i].money_out || 0) - taxAmount
        );

        entries.push({
          journal_id: journalId,
          date,
          company_id: companyId,
          account_id: taxMap.whtPayable,
          description: `${row.description} (WHT Payable)`,
          money_in: 0,
          money_out: taxAmount,
          posting_status: "posted",
          tax_type: "WHT",
          tax_base_amount: Number(row.tax_base_amount || 0),
          tax_rate: Number(row.tax_rate || 0),
          tax_amount: taxAmount,
          taxable_flag: true,
          recoverable_flag: false,
          exempt_flag: false,
          zero_rated_flag: false,
          withholding_flag: true,
          deductible_flag: true,
        });

        continue;
      }
    }

    return entries;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!companyId) {
      alert("Company context not loaded.");
      return;
    }

    if (!date) {
      alert("Please select a date.");
      return;
    }

    const totalIn = journalRows.reduce(
      (sum, row) => sum + Number(row.money_in || 0),
      0
    );

    const totalOut = journalRows.reduce(
      (sum, row) => sum + Number(row.money_out || 0),
      0
    );

    if (round2(totalIn) !== round2(totalOut)) {
      alert(`Entries must balance. Debit (${totalIn}) ≠ Credit (${totalOut}).`);
      return;
    }

    const hasInvalidRows = journalRows.some(
      (row) =>
        !row.account_id ||
        !row.description ||
        (Number(row.money_in || 0) === 0 && Number(row.money_out || 0) === 0)
    );

    if (hasInvalidRows) {
      alert(
        "Each row must have account, description, and either debit or credit amount."
      );
      return;
    }

    const invalidTaxRows = journalRows.some((row) => {
      if (!row.tax_type) return false;
      if (Number(row.tax_base_amount || 0) < 0) return true;
      if (Number(row.tax_rate || 0) < 0) return true;
      if (Number(row.tax_amount || 0) < 0) return true;
      if (row.tax_type === "VAT" && row.withholding_flag) return true;
      return false;
    });

    if (invalidTaxRows) {
      alert("One or more tax rows are invalid. Please review the tax fields.");
      return;
    }

    setLoading(true);

    try {
      const journalId = uuidv4();
      const taxMap = await loadTaxAccountMappings();

      const entries = buildEntriesWithTax(journalRows, journalId, taxMap);

      const finalDebit = round2(
        entries.reduce((sum, row) => sum + Number(row.money_in || 0), 0)
      );
      const finalCredit = round2(
        entries.reduce((sum, row) => sum + Number(row.money_out || 0), 0)
      );

      if (finalDebit !== finalCredit) {
        throw new Error(
          `Final journal is not balanced after tax posting. Debit (${finalDebit}) ≠ Credit (${finalCredit}).`
        );
      }

      const { error } = await supabase.from("journal_entries").insert(entries);

      if (error) {
        console.error("Error saving journal:", error);
        alert(error.message || "Error saving journal.");
      } else {
        alert("Journal saved successfully!");
        setJournalRows([createEmptyRow(), createEmptyRow()]);
        setDate("");
        fetchJournals();
      }
    } catch (err) {
      console.error("Unexpected error saving journal:", err);
      alert(err.message || "Unexpected error saving journal.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="journal-shell">
      {/* Header */}
      <header className="journal-head panel">
        <div className="title">
          <h2>Create Journal</h2>
          <p className="muted">
            <Scale size={14} /> Record balanced debits & credits
          </p>
        </div>

        <div className="head-controls">
          <label className="date-stack">
            <span>
              <CalendarDays size={14} /> Date
            </span>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </label>

          <div
            className={`balance-chip ${
              isBalanced ? "ok" : difference === 0 ? "" : "warn"
            }`}
            title={isBalanced ? "Balanced" : "Debits and Credits must match"}
          >
            {isBalanced ? (
              <CheckCircle2 size={14} />
            ) : (
              <AlertTriangle size={14} />
            )}
            <span>Debit: ₦{totalDebit.toLocaleString()}</span>
            <span>Credit: ₦{totalCredit.toLocaleString()}</span>
            <span>Diff: ₦{difference.toLocaleString()}</span>
          </div>

          <button type="button" className="btn-ghost" onClick={addRows}>
            <PlusSquare size={16} /> Add 2 Rows
          </button>

          <button
            type="submit"
            form="journal-form"
            className="btn-primary"
            disabled={loading}
          >
            <Save size={16} /> {loading ? "Saving…" : "Save Journal"}
          </button>
        </div>
      </header>

      {/* Form */}
      <form id="journal-form" onSubmit={handleSubmit} className="panel">
        <div className="table-wrap">
          <table className="journal-table">
            <thead>
              <tr>
                <th style={{ width: "20%" }}>Account</th>
                <th>Description</th>
                <th className="num-col">Debit (₦)</th>
                <th className="num-col">Credit (₦)</th>
                <th>Tax Type</th>
                <th className="num-col">Tax Rate %</th>
                <th className="num-col">Tax Amount</th>
                <th>Advanced</th>
              </tr>
            </thead>

            <tbody>
              {journalRows.map((row, idx) => (
                <React.Fragment key={idx}>
                  <tr className="jr-row">
                    <td>
                      <select
                        value={row.account_id}
                        onChange={(e) =>
                          handleChange(idx, "account_id", e.target.value)
                        }
                        required
                      >
                        <option value="">Select Account</option>
                        {accounts.map((acct) => (
                          <option key={acct.id} value={acct.id}>
                            {acct.name}
                          </option>
                        ))}
                      </select>
                    </td>

                    <td>
                      <input
                        type="text"
                        placeholder="Narration / description"
                        value={row.description}
                        onChange={(e) =>
                          handleChange(idx, "description", e.target.value)
                        }
                        required
                      />
                    </td>

                    <td className="num-col">
                      <input
                        type="number"
                        inputMode="decimal"
                        min="0"
                        value={row.money_in}
                        onChange={(e) =>
                          handleChange(idx, "money_in", e.target.value)
                        }
                      />
                    </td>

                    <td className="num-col">
                      <input
                        type="number"
                        inputMode="decimal"
                        min="0"
                        value={row.money_out}
                        onChange={(e) =>
                          handleChange(idx, "money_out", e.target.value)
                        }
                      />
                    </td>

                    <td>
                      <select
                        value={row.tax_type}
                        onChange={(e) =>
                          handleChange(idx, "tax_type", e.target.value)
                        }
                      >
                        <option value="">No Tax</option>
                        <option value="VAT">VAT</option>
                        <option value="WHT">WHT</option>
                      </select>
                    </td>

                    <td className="num-col">
                      <input
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.01"
                        value={row.tax_rate}
                        onChange={(e) =>
                          handleChange(idx, "tax_rate", e.target.value)
                        }
                        disabled={!row.tax_type}
                      />
                    </td>

                    <td className="num-col">
                      <input
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.01"
                        value={row.tax_amount}
                        onChange={(e) =>
                          handleChange(idx, "tax_amount", e.target.value)
                        }
                        disabled={!row.tax_type}
                      />
                    </td>

                    <td>
                      {row.tax_type ? (
                        <button
                          type="button"
                          className="btn-ghost"
                          onClick={() =>
                            handleChange(
                              idx,
                              "showTaxAdvanced",
                              !row.showTaxAdvanced
                            )
                          }
                        >
                          {row.showTaxAdvanced ? "Hide" : "Show"}
                        </button>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>

                  {row.tax_type && row.showTaxAdvanced ? (
                    <tr className="jr-row tax-advanced-row">
                      <td colSpan="8">
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns:
                              "repeat(auto-fit, minmax(180px, 1fr))",
                            gap: "10px",
                            padding: "10px 0",
                          }}
                        >
                          <label>
                            Tax Base
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={row.tax_base_amount}
                              onChange={(e) =>
                                handleChange(
                                  idx,
                                  "tax_base_amount",
                                  e.target.value
                                )
                              }
                            />
                          </label>

                          <label>
                            <input
                              type="checkbox"
                              checked={!!row.taxable_flag}
                              onChange={(e) =>
                                handleChange(
                                  idx,
                                  "taxable_flag",
                                  e.target.checked
                                )
                              }
                            />{" "}
                            Taxable
                          </label>

                          <label>
                            <input
                              type="checkbox"
                              checked={!!row.recoverable_flag}
                              onChange={(e) =>
                                handleChange(
                                  idx,
                                  "recoverable_flag",
                                  e.target.checked
                                )
                              }
                            />{" "}
                            Recoverable
                          </label>

                          <label>
                            <input
                              type="checkbox"
                              checked={!!row.exempt_flag}
                              onChange={(e) =>
                                handleChange(
                                  idx,
                                  "exempt_flag",
                                  e.target.checked
                                )
                              }
                            />{" "}
                            Exempt
                          </label>

                          <label>
                            <input
                              type="checkbox"
                              checked={!!row.zero_rated_flag}
                              onChange={(e) =>
                                handleChange(
                                  idx,
                                  "zero_rated_flag",
                                  e.target.checked
                                )
                              }
                            />{" "}
                            Zero Rated
                          </label>

                          <label>
                            <input
                              type="checkbox"
                              checked={!!row.withholding_flag}
                              onChange={(e) =>
                                handleChange(
                                  idx,
                                  "withholding_flag",
                                  e.target.checked
                                )
                              }
                            />{" "}
                            WHT Flag
                          </label>

                          <label>
                            <input
                              type="checkbox"
                              checked={!!row.deductible_flag}
                              onChange={(e) =>
                                handleChange(
                                  idx,
                                  "deductible_flag",
                                  e.target.checked
                                )
                              }
                            />{" "}
                            Deductible
                          </label>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              ))}
            </tbody>

            {/* Sticky footer totals */}
            <tfoot>
              <tr>
                <td colSpan="2" className="totals-label">
                  <span className="muted">
                    <Info size={14} /> Debits must equal Credits
                  </span>
                </td>
                <td className="num-col">
                  <strong>₦{totalDebit.toLocaleString()}</strong>
                </td>
                <td className="num-col">
                  <strong>₦{totalCredit.toLocaleString()}</strong>
                </td>
                <td colSpan="4"></td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Mobile actions (existing feature kept) */}
        <div className="form-actions">
          <button type="button" className="btn-ghost" onClick={addRows}>
            <PlusSquare size={16} /> Add 2 Rows
          </button>
          <button type="submit" className="btn-primary" disabled={loading}>
            <Save size={16} /> {loading ? "Saving…" : "Save Journal"}
          </button>
        </div>
      </form>

      {/* Recent journals preview (existing feature kept) */}
      {Boolean(journals?.length) && (
        <section className="recent panel">
          <h4>Recent Journals</h4>
          <div className="recent-list">
            {journals.slice(0, 6).map((j) => (
              <div key={j.journal_id} className="recent-item">
                <span className="jid">
                  {String(j.journal_id).slice(0, 8).toUpperCase()}
                </span>
                <span className="jdate">
                  {new Date(j.date).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default JournalForm;
